import React, { useState, useEffect } from "react";
import ours from '@oursprivacy/cdp-sdk';

const ContactInfoQuestion = ({ onSubmit, onNext, initialValue }) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const phoneInputRef = React.useRef(null);
  const prevPhoneStateRef = React.useRef({ value: "", cursorPos: 0 });

  // helpers to normalize/format
  const toDigits = (s = "") => s.replace(/\D/g, "");
  const toE164US = (phone) => {
    const d = toDigits(phone);
    if (d.length === 11 && d.startsWith("1")) return `+${d}`;
    if (d.length === 10) return `+1${d}`;
    return null; // not a complete US number
  };
  const formatUSPretty = (digits10) =>
    `(${digits10.slice(0, 3)}) ${digits10.slice(3, 6)}-${digits10.slice(6)}`;

  // Set initial values when editing
  useEffect(() => {
    if (initialValue) {
      setEmail(initialValue.email || "");

      // If we get +1XXXXXXXXXX, show it prettified in the input
      const raw = initialValue.phoneNumber || "";
      const d = toDigits(raw);
      let formatted = "";
      if (d.length === 11 && d.startsWith("1")) {
        formatted = formatUSPretty(d.slice(-10));
      } else {
        formatted = raw;
      }
      setPhoneNumber(formatted);
      prevPhoneStateRef.current = { value: formatted, cursorPos: formatted.length };
    }
  }, [initialValue]);

  // Delayed autofocus to allow smooth transition
  useEffect(() => {
    const timer = setTimeout(() => {
      // Focus without scrolling - we'll handle scroll separately in the parent component
      phoneInputRef.current?.focus({ preventScroll: true });
    }, 300); // Delay to allow previous keyboard to close and transition to complete

    return () => clearTimeout(timer);
  }, []);

  // Function to validate US phone number
  const validatePhoneNumber = (phone) => {
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');

    // Check if it's a complete US phone number (10 digits)
    if (digits.length !== 10) {
      return "Please enter a complete 10-digit phone number";
    }

    // Check if it starts with a valid US area code (2-9)
    const areaCode = parseInt(digits[0]);
    if (areaCode < 2) {
      return "Area code must start with 2-9";
    }

    return ""; // No error
  };

  // Function to validate email address
  const validateEmail = (email) => {
    // Basic email regex pattern
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }

    // Check for common email providers and basic structure
    const [localPart, domain] = email.split('@');

    if (localPart.length < 1 || domain.length < 3) {
      return "Email address is too short";
    }

    if (!domain.includes('.')) {
      return "Email domain must contain a period";
    }

    return ""; // No error
  };

  // Function to automatically format phone number input
  const formatPhoneInput = (digits) => {
    // Limit to 10 digits
    if (digits.length > 10) {
      digits = digits.slice(0, 10);
    }

    // Format with parentheses and dashes
    if (digits.length >= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length >= 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }

    return digits;
  };

  const handlePhoneKeyDown = (e) => {
    // Track cursor position and value before any change
    const input = e.target;
    const cursorPos = input.selectionStart;
    
    // Handle backspace when cursor is right after a formatting character
    if (e.key === 'Backspace' && cursorPos > 0) {
      const charBeforeCursor = phoneNumber[cursorPos - 1];
      const isFormattingChar = charBeforeCursor && !/\d/.test(charBeforeCursor);
      
      // If cursor is right after a formatting character (like "-" or ")"), 
      // delete both the formatting char and the digit before it
      if (isFormattingChar) {
        // Find the digit before this formatting character
        const beforeFormatting = phoneNumber.slice(0, cursorPos - 1);
        const digitsBefore = beforeFormatting.replace(/\D/g, '');
        
        if (digitsBefore.length > 0) {
          // Remove the last digit before the formatting character
          const allDigits = phoneNumber.replace(/\D/g, '');
          const digitIndexToRemove = digitsBefore.length - 1;
          const newDigits = allDigits.slice(0, digitIndexToRemove) + allDigits.slice(digitIndexToRemove + 1);
          const formatted = formatPhoneInput(newDigits);
          
          // Calculate new cursor position
          // Count digits before the removed digit in the new formatted string
          const digitsBeforeRemoved = digitIndexToRemove;
          let newCursorPos = 0;
          let digitCount = 0;
          for (let i = 0; i < formatted.length; i++) {
            if (/\d/.test(formatted[i])) {
              digitCount++;
              if (digitCount === digitsBeforeRemoved) {
                newCursorPos = i + 1;
                break;
              }
            }
          }
          // If no digits left, cursor at start
          if (digitsBeforeRemoved === 0) {
            newCursorPos = 0;
          }
          
          setPhoneNumber(formatted);
          
          // Position cursor correctly
          e.preventDefault();
          setTimeout(() => {
            input.setSelectionRange(newCursorPos, newCursorPos);
            prevPhoneStateRef.current = { value: formatted, cursorPos: newCursorPos };
          }, 0);
          return;
        }
      }
    }
    
    prevPhoneStateRef.current = {
      value: phoneNumber,
      cursorPos: cursorPos
    };
  };

  const handlePhoneChange = (e) => {
    const input = e.target;
    const newValue = input.value;
    const oldValue = prevPhoneStateRef.current.value;
    const oldCursorPos = prevPhoneStateRef.current.cursorPos;

    // Extract digits from the new value
    const newDigits = newValue.replace(/\D/g, '');
    const oldDigits = oldValue.replace(/\D/g, '');

    // Determine if user is deleting or adding
    const isDeleting = newDigits.length < oldDigits.length;

    // Format the new value based on digits only
    const formattedValue = formatPhoneInput(newDigits);
    setPhoneNumber(formattedValue);

    // Calculate new cursor position
    let newCursorPosition = oldCursorPos;
    
    if (isDeleting) {
      // Count digits before the old cursor position
      const digitsBeforeOldCursor = oldValue.slice(0, oldCursorPos).replace(/\D/g, '').length;
      
      // Check what character was at the cursor position before deletion
      const charBeforeCursor = oldValue[oldCursorPos - 1];
      const wasFormattingChar = charBeforeCursor && !/\d/.test(charBeforeCursor);
      
      if (wasFormattingChar) {
        // Deleted a formatting character - position cursor where that char was
        // The digit count stays the same since we only deleted formatting
        const digitsBeforeDeletedChar = digitsBeforeOldCursor;
        
        // Find position in new formatted string
        let digitCount = 0;
        for (let i = 0; i < formattedValue.length; i++) {
          if (/\d/.test(formattedValue[i])) {
            digitCount++;
            if (digitCount === digitsBeforeDeletedChar) {
              newCursorPosition = i + 1;
              break;
            }
          }
        }
      } else {
        // Deleted a digit - position cursor where that digit was
        const digitsBeforeNewCursor = digitsBeforeOldCursor - 1;
        
        // Find position in new formatted string
        let digitCount = 0;
        for (let i = 0; i < formattedValue.length; i++) {
          if (/\d/.test(formattedValue[i])) {
            digitCount++;
            if (digitCount === digitsBeforeNewCursor) {
              newCursorPosition = i + 1;
              break;
            }
          }
        }
        // If no digits left, cursor at start
        if (digitsBeforeNewCursor === 0) {
          newCursorPosition = 0;
        }
      }
    } else {
      // When adding, place cursor after the last digit
      newCursorPosition = formattedValue.length;
    }

    // Update ref for next change
    prevPhoneStateRef.current = { value: formattedValue, cursorPos: newCursorPosition };

    // Set cursor position after state update
    setTimeout(() => {
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);

    // Clear error when user starts typing
    if (phoneError) {
      setPhoneError("");
    }

    // Validate when phone is complete
    if (formattedValue.length === 14) { // (XXX) XXX-XXXX format
      const error = validatePhoneNumber(formattedValue);
      setPhoneError(error);
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);

    // Clear error when user starts typing
    if (emailError) {
      setEmailError("");
    }

    // Validate email as user types (basic validation)
    if (value.length > 5) {
      const error = validateEmail(value);
      setEmailError(error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (phoneNumber.trim() === "" || email.trim() === "") return;

    // Final validation before submission
    const finalPhoneError = validatePhoneNumber(phoneNumber);
    const finalEmailError = validateEmail(email);

    if (finalPhoneError) {
      setPhoneError(finalPhoneError);
      return;
    }

    if (finalEmailError) {
      setEmailError(finalEmailError);
      return;
    }

    // ensure +1XXXXXXXXXX on submit ---
    const e164 = toE164US(phoneNumber);
    if (!e164) {
      setPhoneError("Please enter a complete 10-digit phone number");
      return;
    }

    const answer = { phoneNumber: e164, email };
    onSubmit(answer);

    // Track events when user enters name and email
    if (email.trim()) {
      ours.track('Lead', {}, {
        custom_properties: {
          source: 'conversational intake'
        }
      });
      ours.track('View_content_02', {}, {
        custom_properties: {
          source: 'conversational intake'
        }
      });
    }

    onNext();

    // Reset form
    setPhoneNumber("");
    setEmail("");
    setPhoneError("");
    setEmailError("");
  };

  const canSubmit = phoneNumber.trim() !== "" && email.trim() !== "" && !phoneError && !emailError;

  return (
    <form onSubmit={handleSubmit} className="input-form">
      <div className="dual-input-container">
        <div className="input-group">
          <label htmlFor="phone" className="input-label">Phone Number</label>
          <input
            id="phone"
            type="tel"
            className={`input-field ${phoneError ? 'input-error' : ''}`}
            value={phoneNumber}
            onChange={handlePhoneChange}
            onKeyDown={handlePhoneKeyDown}
            ref={phoneInputRef}
            placeholder="(555) 123-4567"
            maxLength="14"
          />
          {phoneError && (
            <div className="error-message">{phoneError}</div>
          )}
        </div>
        <div className="input-group">
          <label htmlFor="email" className="input-label">Email Address</label>
          <input
            id="email"
            type="email"
            className={`input-field ${emailError ? 'input-error' : ''}`}
            value={email}
            onChange={handleEmailChange}
            placeholder="joe@domain.com"
          />
          {emailError && (
            <div className="error-message">{emailError}</div>
          )}
        </div>
        <button
          type="submit"
          className="send-button dual-input-button"
          disabled={!canSubmit}
        >
          Continue
        </button>
      </div>
    </form>
  );
};

export default ContactInfoQuestion; 